import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useAuth } from '../../context/AuthContext';
import { cn } from '../../lib/utils';
import { messageErreur } from '../../lib/erreurs';
import { nomLisible } from '../../lib/libelles';
import { stationsApi } from '../../lib/stationsApi';
import { SearchableSelect } from '../ui/searchable-select';

/**
 * Choix de la station courante (programme Stations).
 *
 * La liste vient de TOUTES les pages (`stationsApi.stations.all`), cherchable
 * par nom, code, ville ou gérant. Le choix est retenu sur cet appareil
 * (simple confort : sans stockage, la première station est prise).
 */
export const STATION_COURANTE_CLE = 'ecarbu.stations.courante';

export const lireStationCourante = () => {
  try {
    return window.localStorage.getItem(STATION_COURANTE_CLE) || '';
  } catch {
    return '';
  }
};

export const retenirStationCourante = (stationId) => {
  try {
    if (stationId) window.localStorage.setItem(STATION_COURANTE_CLE, stationId);
  } catch {
    // Navigation privée ou stockage refusé : le choix vaut pour cette page seulement.
  }
};

export const stationOption = (station) => ({
  value: station.id,
  label: nomLisible(station.name, station.code),
  description: [station.code, station.city].filter(Boolean).join(' · ') || undefined,
  keywords: [station.code, station.city, station.manager_name].filter(Boolean),
});

/** Anciens rôles station : leurs droits suivent les modules autorisés, lecture comme écriture. */
const ROLES_STATION = ['GESTIONNAIRE_STATION', 'GERANT_STATION', 'AGENT_STATION'];

/**
 * Droits d'écriture des écrans station, mêmes règles que le serveur :
 * administrateur, profil avec le module en écriture, ou ancien rôle station
 * avec le module. Les plafonds nationaux restent à l'administrateur.
 */
export const useDroitsStation = () => {
  const { user, isSuperAdmin, isAdminSociete, hasModule, canWriteModule, canManageUsers } = useAuth();
  const ancienRole = ROLES_STATION.includes(user?.role);
  const peutEcrire = (code) => Boolean(canWriteModule(code) || (ancienRole && hasModule(code)));
  return {
    peutEcrire,
    peutLire: (code) => Boolean(hasModule(code)),
    estAdministrateur: Boolean(isSuperAdmin || isAdminSociete),
    peutChoisirGerant: Boolean(canManageUsers),
  };
};

/** Toutes les stations de la société, rechargeables. */
export const useStations = ({ includeArchived = false } = {}) => {
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setStations((await stationsApi.stations.all({ includeArchived })) || []);
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible de charger les stations'));
    } finally {
      setLoading(false);
    }
  }, [includeArchived]);
  useEffect(() => {
    reload();
  }, [reload]);
  return { stations, loading, reload };
};

export const StationPicker = ({ stations = [], value, onChange, className, disabled }) => {
  const options = useMemo(
    () => stations.filter((station) => !station.archived_at).map(stationOption),
    [stations],
  );

  // Une seule station, ou une station retenue : elle est choisie d'office.
  useEffect(() => {
    if (!options.length) return;
    if (value && options.some((option) => option.value === value)) return;
    const retenue = lireStationCourante();
    const choix = options.find((option) => option.value === retenue) || options[0];
    onChange(choix.value);
  }, [options, value, onChange]);

  const choisir = (stationId) => {
    retenirStationCourante(stationId);
    onChange(stationId);
  };

  return (
    <SearchableSelect
      value={value}
      onValueChange={choisir}
      options={options}
      aria-label="Station"
      placeholder="Choisir la station"
      searchPlaceholder="Rechercher une station…"
      emptyMessage="Aucune station"
      disabled={disabled || !options.length}
      className={cn('h-9 w-full sm:w-64', className)}
    />
  );
};

export default StationPicker;
